import React, { useState } from 'react';
import {
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Menu,
    MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSpendType, useEditSpendType, useDeleteSpendType } from '../../hooks/useSpendType';
import { usePermission } from '../../hooks/usePermission';
import DataTable from '../shared/DataTable';
import { EditSpendTypesPermission, DeleteSpendTypesPermission } from '../../constants/permissions';
import { useEventType } from '../../hooks/useEventType';


const SpendTypeTable = () => {
    const { data: spendTypes, isLoading } = useSpendType();
    const editSpendType = useEditSpendType();
    const deleteSpendType = useDeleteSpendType();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedSpendType, setSelectedSpendType] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        description: '',
        event_type_id: ''

    });
    const { data: EventTypes } = useEventType();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedActionId, setSelectedActionId] = useState(null);

    const { data: canEditSpendType } = usePermission({
        permissionName: EditSpendTypesPermission
    });

    const { data: canDeleteSpendType } = usePermission({
        permissionName: DeleteSpendTypesPermission
    });

    const sortedSpendTypeData = spendTypes?.slice().sort((a, b) => {
        const numA = parseInt(a.name.match(/\d+/)?.[0] || 0, 10);
        const numB = parseInt(b.name.match(/\d+/)?.[0] || 0, 10);
        return numA - numB;
    });


    const handleMenuClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setSelectedActionId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedActionId(null);
    };

    const handleEditClick = (spendType) => {
        setSelectedSpendType(spendType);
        setEditFormData({
            name: spendType.name,
            description: spendType.description || '',
            event_type_id: spendType.event_type_id || '',

        });
        setEditDialogOpen(true);
        handleMenuClose();
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setSelectedSpendType(null);
    };

    const handleEditSubmit = async () => {
        try {
            await editSpendType.mutateAsync({
                id: selectedSpendType.id,
                data: editFormData,
            });
            handleEditClose();
        } catch (error) {
            console.error('Error editing spend type:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this spend type?')) {
            try {
                await deleteSpendType.mutateAsync(id);
                handleMenuClose();
            } catch (error) {
                console.error('Error deleting spend type:', error);
            }
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Description',
            selector: row => row.description || '',
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
                    <MoreVertIcon />
                </IconButton>
            ),
            width: '100px',
        },
    ];

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <DataTable
                columns={columns}
                data={sortedSpendTypeData}
                searchFields={['name', 'description']}
                searchPlaceholder="Search spend types..."
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {canEditSpendType && (
                    <MenuItem onClick={() => handleEditClick(sortedSpendTypeData.find(st => st.id === selectedActionId))}>
                        <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
                        <h6 className='text-blueColor'>Edit</h6>
                    </MenuItem>
                )}
                {canDeleteSpendType && (
                    <MenuItem onClick={() => handleDelete(selectedActionId)}>
                        <i className="fa-solid fa-trash text-red-600 mr-2" />
                        <h6 className='text-blueColor'>Delete</h6>
                    </MenuItem>
                )}
            </Menu>

            <Dialog
                open={editDialogOpen}
                onClose={handleEditClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className="bg-blueColor text-white py-4">
                    <i className="fas fa-edit text-xl mx-2"></i>
                    <span className="text-xl font-semibold">Edit Spend Type</span>
                </DialogTitle>
                <DialogContent className="mt-4">
                    <div className="grid grid-cols-1 gap-4 p-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Name
                            </label>
                            <input
                                name="name"
                                value={editFormData.name}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, name: e.target.value })
                                }
                                type="text"
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={editFormData.description}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, description: e.target.value })
                                }
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                                rows="4"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Event Type
                            </label>
                            <select
                                name="event_type_id"
                                value={editFormData.event_type_id}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, event_type_id: e.target.value })}
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                            >
                                <option value="">Select Event Type</option>
                                {EventTypes?.map((EventType) => (
                                    <option key={EventType.id} value={EventType.id}>
                                        {EventType.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                    </div>
                    <div className="flex justify-end gap-4 mt-6 p-4">
                        <button
                            onClick={handleEditClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleEditSubmit}
                            className="px-4 py-2 bg-blueColor text-white rounded"
                        >
                            Save
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SpendTypeTable; 